/** @jsxImportSource preact */
import type {
  CustomerAuth,
  VerifyEmailAction,
} from "../protocol/customer-auth";
import type { ComponentChildren, ComponentProps } from "preact";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "preact/hooks";

import {
  readReplyContent,
  type FeedbackSnapshot,
  type Settings,
} from "@go-novus/admin-api";
import type { MerchantFeedbackClient } from "../config";
import type { OrderActionFailure } from "../conversation/order-failure";

import * as m from "../paraglide/messages.js";
import { Composer, COMPOSER_STYLES } from "./composer";
import { SupportRequestPanel } from "./contact/support-request-panel";
import { SupportNotice } from "./contact/support-feedback";
import { ArrowDownIcon } from "./icons";
import {
  LauncherCapsule,
  proactiveLauncherPrompt,
  type LauncherPrompt,
} from "./launcher-capsule";
import {
  resolveMessage,
  type WidgetCartReceipt,
  type WidgetChatMessage,
} from "./message";
import {
  deriveTheme,
  withAlpha,
  type WidgetDirection,
  type WidgetTheme,
  type WidgetRadius,
  type WidgetSurface,
} from "./theme";
import { MESSAGE_MARKDOWN_STYLES, readPlainText } from "./message-markdown";
import { MessageActions, MESSAGE_ACTION_STYLES } from "./message-actions";
import { Marker, MarkerContent, MESSAGE_STYLES } from "./message-parts";
import { PanelHeader, PANEL_HEADER_STYLES } from "./panel-header";
import { QuickReplies } from "./quick-replies";
import { useComposerSize } from "./use-composer-size";
import { useIsMobile } from "./use-is-mobile";
import {
  MessageScroller,
  useMessageScroller,
  useMessageScrollerScrollable,
} from "./message-scroller/primitive.mjs";
import { GeneratingText, WidgetMessage } from "./widget-message";
import type { CharacterReaction } from "./use-character-reaction";
import type { LauncherDrag } from "./use-launcher-drag";
import {
  characterVariantFor,
  WidgetCharacter,
  type WidgetCharacterVariant,
} from "./widget-character";
import {
  DEFAULT_PANEL_SIZE,
  PanelResize,
  type PanelSize,
} from "./panel-resize";
import type { WidgetView } from "./view-state";

import type { StorefrontCart } from "../cart/cart";
import { useCartSheet } from "./cart/cart-controller";
import {
  PanelSheetHostContext,
  type PanelSheetHost,
} from "./sheet/panel-sheet";
import { CartReceipt } from "./cart/cart-receipt";
import type { ProactivePrompt } from "../proactive/proactive";
import type { VoiceStatus } from "../voice/controller";

/** 聊天窗收起时这一轮会话的进展：占主动提示的位置，用商家选的主动提示形态。 */
export type ReplyNotice =
  | { status: "generating"; progress?: string }
  | { status: "replied"; text: string; dismiss: () => void }
  | { status: "failed"; dismiss: () => void };

/**
 * 挂件的展示状态：收起入口、入口上方的主动提示或回复提示、聊天窗；沿用现有会话和提示开合契约。
 */
export type WidgetCapsule =
  | { size: "small" }
  | { size: "medium"; prompt: ProactivePrompt }
  | { size: "medium"; notice: ReplyNotice }
  | { size: "large" };

export interface WidgetPanelProps {
  /** 按钮发起业务卡操作时递增；发送新问题时归零，历史回放不触发跟随。 */
  cardAction?: number;
  storefront?: StorefrontCart;
  /** Shopify 确认加购或改数量后插回执；不给就不摆加购。 */
  onCartReceipt?: (receipt: WidgetCartReceipt) => void;
  drawerHandle?: ComponentChildren;
  fullscreen?: boolean;
  onToggleFullscreen?: () => void;
  accent?: string;
  onPrimary?: string;
  /** 空值或不含 `/`、`.` 的短字符串按字母头像渲染，否则当图片地址。 */
  avatar?: string;
  blur?: number;
  busy?: boolean;
  /** 角色画法，对应商家配置 `chatbox.character`。水彩玻璃的眼睛恒为白色，主色太浅压不住时退回实色。 */
  character?: WidgetCharacterVariant;
  /** 当前形态，默认大胶囊：后台预览和看图页只画聊天窗。 */
  capsule?: WidgetCapsule;
  /**
   * 这次打开聊天窗的来历：`open` 是买家点开的，走展开时间轴（设计稿 10a）；
   * `restore` 是换页、刷新时聊天窗本来就开着，首帧摆在原位，消息从骨架醒来（设计稿 12a）。
   */
  entrance?: "open" | "restore";
  /** 为 true 时消息区留空，身份定下后才记录开窗消息。 */
  /** 买家从聊天窗里点了跳转、页面还没换走：最新回复的角色合眼打盹。 */
  leaving?: boolean;
  /** 给了才记住并恢复消息区滚动位置和输入框光标；店面挂载根传入，预览和分镜不传。 */
  view?: WidgetView;
  draft?: string;
  error?: string;
  onDraftChange?: (draft: string) => void;
  direction?: WidgetDirection;
  /** 还没有任何消息：摆问候语和建议问题，不摆气泡。 */
  empty?: boolean;
  emptyTitle?: string;
  font?: string;
  fontSize?: string;
  height?: string;
  /** undefined 恢复桌面自适应默认尺寸。 */
  onResize?: (size: PanelSize | undefined) => void;
  /** 预览保留店面缩放手柄的标题栏留白，但不提供拖拽行为。 */
  reserveResizeSpace?: boolean;
  showLauncher?: boolean;
  launcherLabel?: string;
  /** 给了就能把收起入口拖到左右两边，吸附和记住位置由挂载根负责。 */
  launcherDrag?: LauncherDrag;
  messages?: WidgetChatMessage[];
  /** 占满宿主盒子：无圆角、无投影、无 launcher。移动抽屉和后台 Playground 都是这一种。 */
  fill?: boolean;
  onClear?: () => void;
  onClose?: () => void;
  /** 给了才画商家反馈。店面不传。 */
  merchantFeedback?: MerchantFeedbackClient;
  onOpen?: () => void;
  onQuickReply?: (label: string) => void;
  /** 未送达那条气泡上的重试，收到的是那条消息的原文和它自己的 id。 */
  onRetry?: (text: string, msgId: string) => void;
  onOrderAction?: (
    messageId: string,
    stateIndex: number,
    action: "select" | "more" | "not_received" | "check",
    orderRef?: string,
  ) => void;
  orderBusy?: boolean;
  orderFailures?: Record<string, OrderActionFailure>;
  onVerifyTask?: (messageId: string, email?: string) => void;
  verificationBusy?: boolean;
  /** Playground 没有店铺登录:登录卡改成填店内顾客的邮箱。 */
  playgroundVerification?: boolean;
  verificationAuth?: CustomerAuth;
  onVerifyEmail?: (
    messageId: string,
    input: Parameters<VerifyEmailAction>[0],
  ) => ReturnType<VerifyEmailAction>;
  onSend?: (text: string) => void;
  /** 入口太靠上、聊天窗在它上方放不下时，聊天窗底边比入口底边低出的距离。 */
  panelOffset?: number;
  placeholder?: string;
  position?: "left" | "right";
  proactivePromptStyle?: Settings["chatbox"]["proactivePromptStyle"];
  poweredBy?: boolean;
  voiceEnabled?: boolean;
  voiceStatus?: VoiceStatus;
  voiceError?: string;
  onVoiceToggle?: () => void;
  /** 摆在会话最后的快捷回答，点了走 onQuickReply。 */
  quickReplies?: string[];
  /** 会话里刚发生的一次性反应，由挂载根按真实会话算好传入。 */
  reaction?: CharacterReaction;
  /** 纯展示状态：目前仅分镜使用，真实 Conversation 尚无等待补充协议。 */
  waitingLabel?: string;
  radius?: WidgetRadius;
  /** 开着才在每组 AI 回复下方摆复制按钮；店面跟着商家在 Settings 里的开关走（GON-179）。 */
  replyCopyButton?: boolean;
  /** 标题下的状态行，默认「Replies instantly」；传空字符串不画。 */
  subtitle?: string;
  /** 首屏的建议问题，来自商家配置；空数组整块不画。 */
  suggestions?: string[];
  surface?: WidgetSurface;
  surfaceOpacity?: number;
  title?: string;
  width?: string;
}

/** 建议问题逐条渐入的起点与步长。 */
const SUGGESTION_DELAY_BASE_MS = 400;
const SUGGESTION_DELAY_STEP_MS = 60;
/** 任一条建议问题超过这么多字符，胶囊折行后参差不齐，整组改成左对齐竖排。 */
const SUGGESTION_PILL_MAX_CHARS = 30;
/** 离底部不超过这么多就算在底部：不出现回到最新，消息区变矮时也保持贴底。 */
const SCROLL_EDGE_THRESHOLD = 24;
/** 设计稿 11a：回复流完 320ms 后反馈行浮起，角色同时眨一次眼（animations.ts 里按同一时刻写）。 */
const REPLY_ACTIONS_DELAY = "320ms";
/** 快捷回答跟在末条消息后 400ms 渐入。 */
const QUICK_REPLY_DELAY = "400ms";
/** 回复提示最多留这么多字：画面上只露三行，但按钮名称和读屏播报用的是整段。 */
const REPLY_PREVIEW_MAX_CHARS = 200;

export function readReplyPreview(text: string): string {
  const chars = Array.from(
    readPlainText(
      readReplyContent(text)
        .blocks.flatMap((block) => (block.type === "text" ? [block.text] : []))
        .join("\n\n"),
    ),
  );
  if (chars.length === 0) return m.widget_reply_notice_new();
  return chars.length > REPLY_PREVIEW_MAX_CHARS
    ? `${chars.slice(0, REPLY_PREVIEW_MAX_CHARS).join("")}…`
    : chars.join("");
}

function MessageViewport(
  props: ComponentProps<typeof MessageScroller.Viewport>,
) {
  const { end } = useMessageScrollerScrollable();
  const viewportRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    let height = viewport.clientHeight;
    const observer = new ResizeObserver(() => {
      const shrunk = height - viewport.clientHeight;
      height = viewport.clientHeight;
      if (shrunk <= 0) return;
      // 输入框撑高、抽屉或面板变小前在底部，就留在底部，免得最后几行被盖住；上翻阅读时不动。
      const gapBeforeShrink =
        viewport.scrollHeight - height - viewport.scrollTop - shrunk;
      if (gapBeforeShrink <= SCROLL_EDGE_THRESHOLD)
        viewport.scrollTop += shrunk;
    });
    observer.observe(viewport);
    return () => {
      observer.disconnect();
    };
  }, []);
  return (
    <MessageScroller.Viewport
      {...props}
      ref={viewportRef}
      data-scroll-end={end || undefined}
    />
  );
}

function MessageLatestButton(
  props: ComponentProps<typeof MessageScroller.Button>,
) {
  const { scrollToEnd } = useMessageScroller();
  const onClick: NonNullable<typeof props.onClick> = (event) => {
    props.onClick?.(event);
    if (event.defaultPrevented) return;
    event.preventDefault();
    // 点击时读取偏好，面板打开后切换系统设置也立即生效。
    scrollToEnd({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  };
  return <MessageScroller.Button {...props} onClick={onClick} />;
}

/**
 * 刚插进来的加购回执滚到眼前：买家可能是在上面翻到的商品卡上加的购，回执却接在会话末尾。
 * 打开聊天窗时就在的回执不动，滚动位置照旧。
 */
function FollowCartReceipt({ id }: { id?: string }) {
  const { scrollToMessage } = useMessageScroller();
  const seen = useRef(id);
  useEffect(() => {
    if (id === undefined || id === seen.current) return;
    seen.current = id;
    scrollToMessage(id, {
      align: "end",
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  }, [id]);
  return null;
}

function FollowActionCards({
  action,
  messages,
}: {
  action: number;
  messages: WidgetChatMessage[];
}) {
  const { scrollToMessage } = useMessageScroller();
  const previous = useRef(new Map<string, string>());
  const seen = useRef(action);
  const pending = useRef(false);
  useEffect(() => {
    if (action !== seen.current) {
      pending.current = action !== 0;
      seen.current = action;
    }
    let target: WidgetChatMessage | undefined;
    const next = new Map<string, string>();
    for (const message of messages) {
      const signature = JSON.stringify([
        message.supportOpening,
        message.blocks,
      ]);
      next.set(message.id, signature);
      if (
        pending.current &&
        previous.current.get(message.id) !== signature &&
        (message.supportOpening || message.blocks?.length)
      )
        target = message;
    }
    previous.current = next;
    if (!target) return;
    scrollToMessage(target.id, {
      align: "end",
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
    if (!target.supportOpening?.pending) pending.current = false;
  }, [action, messages]);
  return null;
}

/** 面板根节点上的 CSS 变量：消息、操作行、生成中短语、骨架和输入框都从这里取主题色。 */
export function panelStyleVars(theme: WidgetTheme) {
  return {
    "--gn-primary": theme.accent,
    "--gn-shimmer-highlight": theme.shimmerHighlight,
    "--gn-panel": theme.panelBg,
    "--gn-surface-solid": theme.surfaceSolid,
    "--gn-field": theme.field,
    "--gn-ink": theme.ink,
    "--gn-line": theme.line,
    "--gn-action": theme.faint,
    "--gn-action-accent": theme.accentFill,
    "--gn-action-accent-fg": theme.accentFillFg,
    "--gn-action-hover": theme.subtle,
    "--gn-action-success": theme.successText,
    "--gn-danger": theme.dangerText,
    "--gn-character-edge": theme.characterEdge,
    "--gn-divider": theme.divider,
    "--gn-panel-radius": `${String(theme.panelRadius)}px`,
    "--gn-placeholder": theme.placeholder,
    "--gn-placeholder-style": theme.serif ? "italic" : "normal",
    "--gn-shimmer-base": theme.placeholder,
    "--gn-skeleton": theme.bubbleBg,
    "--gn-skeleton-hi": `color-mix(in srgb, ${theme.bubbleBg}, #fff ${theme.dark ? "8%" : "55%"})`,
    "--gn-tip-bg": theme.ink,
    "--gn-tip-fg": theme.dark ? "#1C1B1A" : "#FFFFFF",
  };
}

/**
 * 店面挂件的面板。颜色、圆角、气质全部由 props 派生，字体默认 inherit 宿主页面——
 * 挂件只带结构与交互，皮肤借店铺的（见 Widget.dc.html 的设计说明）。
 */
export function WidgetPanel({
  cardAction = 0,
  storefront,
  onCartReceipt,
  drawerHandle,
  fullscreen = false,
  onToggleFullscreen,
  accent,
  onPrimary,
  avatar = "",
  blur,
  busy = false,
  character = "solid",
  capsule = { size: "large" },
  entrance = "open",
  leaving = false,
  view,
  draft: controlledDraft,
  error,
  onDraftChange,
  direction,
  empty = false,
  emptyTitle = m.widget_empty_title(),
  font = "inherit",
  fontSize = "14px",
  height = `${String(DEFAULT_PANEL_SIZE.height)}px`,
  onResize,
  reserveResizeSpace = onResize !== undefined,
  showLauncher = true,
  launcherLabel = m.widget_launcher_label(),
  launcherDrag,
  messages = [],
  fill = false,
  onClear,
  onClose,
  merchantFeedback,
  onOpen,
  onQuickReply,
  onRetry,
  onOrderAction,
  orderBusy,
  orderFailures,
  onVerifyTask,
  verificationBusy,
  playgroundVerification,
  verificationAuth,
  onVerifyEmail,
  onSend,
  panelOffset = 0,
  placeholder = m.widget_composer_placeholder(),
  position = "right",
  proactivePromptStyle = "bubble",
  poweredBy = true,
  voiceEnabled,
  voiceStatus,
  voiceError,
  onVoiceToggle,
  quickReplies = [],
  reaction,
  waitingLabel,
  radius,
  replyCopyButton = false,
  subtitle = m.widget_header_status(),
  suggestions = [],
  surface,
  surfaceOpacity,
  title = m.widget_header_title(),
  width = `${String(DEFAULT_PANEL_SIZE.width)}px`,
}: WidgetPanelProps) {
  const mobile = useIsMobile();
  const [localDraft, setLocalDraft] = useState("");
  const [feedbackSnapshots, setFeedbackSnapshots] = useState<
    Record<string, FeedbackSnapshot>
  >({});
  const feedbackKey = (merchantFeedback ? messages : [])
    .filter(
      (message) => message.role === "ai" && message.text && !message.typing,
    )
    .map((message) => message.id)
    .join("\n");
  useEffect(() => {
    if (!merchantFeedback || feedbackKey === "") return;
    let cancelled = false;
    void merchantFeedback.load(feedbackKey.split("\n")).then(
      (rows) => {
        if (cancelled) return;
        setFeedbackSnapshots(
          Object.fromEntries(rows.map((row) => [row.messageId, row])),
        );
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [merchantFeedback, feedbackKey]);
  const draft = controlledDraft ?? localDraft;
  const setDraft = onDraftChange ?? setLocalDraft;
  const theme = deriveTheme({
    accent,
    onPrimary,
    blur,
    direction,
    font,
    radius,
    surface,
    surfaceOpacity,
  });
  const cart = useCartSheet({
    storefront,
    onReceipt: onCartReceipt,
    theme,
    touch: mobile,
  });
  // 消息里的卡片要开面板或浮层时挂到聊天窗这一层，才能盖住整个聊天窗。
  const [panelElement, setPanelElement] = useState<HTMLElement | null>(null);
  const sheetHost = useMemo<PanelSheetHost | undefined>(
    () =>
      panelElement
        ? {
            element: panelElement,
            touch: mobile,
            background: theme.surfaceSolid,
          }
        : undefined,
    [panelElement, mobile, theme.surfaceSolid],
  );
  const alignRight = position !== "left";
  const reserveHeaderSpace = !fill && reserveResizeSpace;
  const renderLauncher = !fill && showLauncher;
  const characterRadius = `${String(Math.min((radius ?? 8) * 1.8, 20))}px`;
  const panelCharacter = characterVariantFor(character, theme.accentFill);
  // 入口和主动提示压在店面上，用商家原色判断。
  const entryCharacter = characterVariantFor(character, theme.accent);
  // 设计稿 6a：商家开了毛玻璃，或角色本身是水彩玻璃，入口上方的气泡就跟着用同一种半透明材质。
  const glassBubble = theme.glass || entryCharacter === "watercolor";
  const open = capsule.size === "large";
  const [present, setPresent] = useState(open);
  useLayoutEffect(() => {
    if (open) {
      setPresent(true);
      return;
    }
    // 保留退出期间的内容；取消旧计时器，使快速反向开合不会卸载新面板。时长跟 animations.ts 里收起聊天窗的过渡一致。
    const timeout = setTimeout(
      () => {
        setPresent(false);
      },
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 150 : 300,
    );
    return () => {
      clearTimeout(timeout);
    };
  }, [open]);
  const inputRef = useComposerSize(draft, open && present, font, fontSize);
  // 聊天窗收完再出提示：收起动画里入口高度是 0，提示会先掉到入口下面再跳上去。
  const medium = capsule.size === "medium" && !present ? capsule : undefined;
  const notice = medium && "notice" in medium ? medium.notice : undefined;
  const repliedText = notice?.status === "replied" ? notice.text : undefined;
  const replyPreview = useMemo(
    () => (repliedText === undefined ? "" : readReplyPreview(repliedText)),
    [repliedText],
  );
  const failedText = m.widget_reply_notice_failed();
  const prompt: LauncherPrompt | undefined =
    medium && "prompt" in medium
      ? proactiveLauncherPrompt(medium.prompt, m.widget_proactive_dismiss())
      : notice?.status === "generating"
        ? {
            text: <GeneratingText progress={notice.progress} />,
            label: m.widget_message_replying(),
            activate: onOpen,
            motion: "looking",
          }
        : notice && {
            text:
              notice.status === "failed" ? (
                failedText
              ) : (
                <span class="gn-reply-preview">{replyPreview}</span>
              ),
            label: notice.status === "failed" ? failedText : replyPreview,
            activate: onOpen,
            dismiss: {
              label: m.widget_reply_notice_dismiss(),
              onClick: notice.dismiss,
            },
            motion:
              notice.status === "failed"
                ? "blocked"
                : reaction === "done"
                  ? "done"
                  : "still",
          };
  const bodyPrompt = prompt !== undefined && proactivePromptStyle === "body";
  // 请求失败那一行接在处理中那一行的位置，角色原地换成失败表情。
  const visibleMessages: WidgetChatMessage[] =
    busy && !messages.some((message) => message.typing)
      ? [...messages, { id: "pending", role: "ai", typing: true }]
      : !busy && error
        ? [
            ...messages,
            {
              id: "error",
              role: "ai",
              alert: true,
              text: error,
              textFormat: "plain",
            },
          ]
        : messages;
  // 末条消息的状态由它所在组的组首角色来演。系统提示不是谁说的话，排在最后也不抢走角色。
  let lastSpoken = visibleMessages.length - 1;
  while (visibleMessages[lastSpoken]?.role === "system") lastSpoken -= 1;
  let activeAvatar = lastSpoken;
  while (
    visibleMessages[activeAvatar]?.role === "ai" &&
    visibleMessages[activeAvatar - 1]?.role === "ai"
  )
    activeAvatar -= 1;
  // 身份定下后的首帧才记开窗消息；否则刷新接管的历史会被当成新回复逐字流出。
  // 在渲染里记，不能等 effect：新消息挂上的第一帧就要拿对延迟。离开列表的 id 要删掉：
  // 生成中、请求失败这两行每轮都用同一个 id，不删的话下一轮会被当成开窗时就在的消息。
  const openedIds = useRef(new Set<string>());
  const openedWith = useRef(entrance);
  const landingId = useRef<string>();
  const wasOpen = useRef(false);
  const visibleIds = new Set(visibleMessages.map((message) => message.id));
  const turnId = visibleMessages.reduce(
    (id, message) => (message.role === "user" ? message.id : id),
    visibleMessages[0]?.id,
  );
  // 换页、刷新后醒来的这一轮贴底跟随（GON-296）；每次开窗重记，收起再点开就是正常展开。
  const wakeTurn = useRef<string>();
  if (open && !wasOpen.current) {
    openedIds.current = visibleIds;
    openedWith.current = entrance;
    wakeTurn.current = entrance === "restore" ? turnId : undefined;
    const landing = visibleMessages[activeAvatar];
    landingId.current = landing?.role === "ai" ? landing.id : undefined;
  } else
    for (const id of openedIds.current)
      if (!visibleIds.has(id)) openedIds.current.delete(id);
  wasOpen.current = open;
  const waking = openedWith.current === "restore";
  // 醒来时从最新一条往上数，系统提示另有自己的出场时刻，不占位次。
  const wakeOrder: number[] = [];
  for (let index = visibleMessages.length - 1, order = 0; index >= 0; index--) {
    wakeOrder[index] = order;
    if (visibleMessages[index]?.role !== "system") order += 1;
  }
  // 连续 AI 回复算一组：角色只画在组首，操作行只挂在组末，复制的是整组正文。
  // 生成中那条只是进度，不进复制内容，也不挂操作行。
  let groupText: string[] = [];
  const rows = visibleMessages.map((message, index) => {
    const live = !openedIds.current.has(message.id);
    const resolved = resolveMessage(
      message,
      theme,
      live
        ? undefined
        : waking
          ? { order: wakeOrder[index] ?? 0, wake: true }
          : { order: index },
    );
    const ai = message.role === "ai" && !message.typing && !message.alert;
    const next = visibleMessages[index + 1];
    const groupStart =
      message.role === "ai" && visibleMessages[index - 1]?.role !== "ai";
    if (groupStart) groupText = [];
    if (ai)
      for (const block of resolved.blocks)
        if (block.type === "text" && block.text !== "")
          groupText.push(block.text);
    const groupEnd =
      ai &&
      (next?.role !== "ai" || next.typing === true || next.alert === true);
    return {
      resolved,
      groupStart,
      turnStart: index > 0 && visibleMessages[index - 1]?.role !== message.role,
      copyText: groupEnd ? groupText.join("\n\n") : "",
      live,
      landing: message.id === landingId.current,
      reveal: live && ai,
    };
  });
  const lastMessage = visibleMessages[lastSpoken];
  const avatarMotion = leaving
    ? "dozing"
    : lastMessage?.typing
      ? reaction === "ack"
        ? "ack"
        : "looking"
      : lastMessage?.alert
        ? "blocked"
        : waitingLabel
          ? "waiting"
          : "quiet";
  let lastReceiptId: string | undefined;
  for (const message of visibleMessages)
    if (message.cartReceipt) lastReceiptId = message.id;
  // 跟随由买家点「回到最新」开启；新问题开启新一轮阅读，不继承上一轮贴底。
  // 换页、刷新后醒来固定停在最新一条（GON-296）：不还原离开时的位置，并贴底跟随这一轮，
  // 卡片数据晚到撑高也不会把最新消息挤出视口；买家自己往上翻就停止跟随。
  const [followedTurn, setFollowedTurn] = useState<string>();
  const following =
    turnId !== undefined &&
    (followedTurn === turnId || wakeTurn.current === turnId);
  const panelRoot = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const syncVisibility = () => {
      panelRoot.current?.toggleAttribute("data-motion-paused", document.hidden);
    };
    syncVisibility();
    document.addEventListener("visibilitychange", syncVisibility);
    return () => {
      document.removeEventListener("visibilitychange", syncVisibility);
    };
  }, []);
  // 换页前记下光标。用 pagehide：换页、刷新、iOS 都会触发；beforeunload 在 iOS 上不可靠，还会让 Firefox 放弃往返缓存。
  useEffect(() => {
    if (!view || !open) return;
    const save = () => {
      const input = inputRef.current;
      const root = input?.getRootNode();
      const focused =
        (root instanceof ShadowRoot || root instanceof Document) &&
        root.activeElement === input;
      view.save({
        composer:
          input && focused
            ? { start: input.selectionStart, end: input.selectionEnd }
            : undefined,
      });
    };
    window.addEventListener("pagehide", save);
    return () => {
      window.removeEventListener("pagehide", save);
    };
  }, [view, open, inputRef]);
  // 手机上不恢复焦点：一聚焦就弹键盘，买家还没看到新页面就被挡住了。
  useLayoutEffect(() => {
    const caret = view?.initial.composer;
    const input = inputRef.current;
    if (entrance !== "restore" || mobile || !caret || !input) return;
    input.focus({ preventScroll: true });
    input.setSelectionRange(caret.start, caret.end);
  }, []);

  const canSend = !busy && draft.trim() !== "";

  function submit(): void {
    if (canSend && onSend !== undefined) {
      onSend(draft.trim());
      if (controlledDraft === undefined) setDraft("");
    }
  }

  const scrollStyle = {
    overscrollBehavior: "contain",
    scrollbarColor: `${theme.line} transparent`,
    scrollbarWidth: "thin",
  } as const;

  return (
    <div
      ref={panelRoot}
      data-gn-gaze-area
      style={{
        ...panelStyleVars(theme),
        alignItems: alignRight ? "flex-end" : "flex-start",
        color: theme.ink,
        display: "flex",
        flexDirection: "column",
        fontFamily: font,
        fontSize,
        gap: present ? 0 : 12,
        height: fill ? "100%" : "auto",
        lineHeight: 1.5,
        minHeight: 0,
        width: fill ? "100%" : width,
      }}
    >
      <style>{MESSAGE_MARKDOWN_STYLES}</style>
      <style>{MESSAGE_STYLES}</style>
      <style>{MESSAGE_ACTION_STYLES}</style>
      <style>{PANEL_HEADER_STYLES}</style>
      <style>{COMPOSER_STYLES}</style>
      <style>{`
        .gn-message-viewport {
          mask-image: linear-gradient(#000 calc(100% - var(--gn-fade-end, 0px)), transparent);
          -webkit-mask-image: linear-gradient(#000 calc(100% - var(--gn-fade-end, 0px)), transparent);
        }
        .gn-message-viewport[data-scroll-end] { --gn-fade-end: min(12%, 40px); }
        .gn-message-viewport[data-pending-scroll] { visibility: hidden; }
        .gn-message-viewport > [role="log"] > [data-message-id]:first-child { margin-top:auto; }
        .gn-scroll-latest { display: grid; place-items: center; }
        .gn-scroll-latest[data-active="false"] { display: none; }
        /* 同一组消息之间 12px，换人说话 20px；Editorial 收紧成 8px，换人时加一条细线。 */
        .gn-chat-panel { --gn-group-gap:12px; --gn-turn-gap:20px; }
        .gn-chat-panel[data-editorial] { --gn-group-gap:8px; --gn-turn-gap:16px; }
        /* 消息入场从下方 12px 浮起，请求卡再叠一层位移；位移会算进可滚动高度，内容正好填满时展开瞬间多出几像素、冒出滚动条。
           列表本身撑满视口并留有内边距，裁掉位移溢出不会切到任何静止内容。 */
        .gn-message-list { overflow:clip; }
        .gn-message-list:has(.gn-feedback[data-open]) { overflow:visible; }
        .gn-message-list > [data-message-id] + [data-message-id] { margin-top:var(--gn-group-gap); }
        .gn-message-list > [data-message-id] + [data-message-id][data-turn-start] { margin-top:var(--gn-turn-gap); }
        .gn-chat-panel[data-editorial] .gn-message-list > [data-message-id] + [data-message-id][data-turn-start] { border-top:1px solid var(--gn-divider); padding-top:16px; }
        /* 抽屉高度只给输入区用；档位动画时它每帧都变，消息列表继承了就会整段重算样式。 */
        .gn-message-viewport { --gn-drawer-body-height:initial; }
      `}</style>
      {present && (
        <PanelSheetHostContext.Provider value={sheetHost}>
          <section
            ref={setPanelElement}
            class="gn-chat-panel"
            data-color-scheme={theme.dark ? "dark" : "light"}
            data-open={open}
            data-entrance={waking ? "restore" : undefined}
            data-fill={fill || undefined}
            data-editorial={theme.editorial || undefined}
            data-position={position}
            aria-hidden={!open || undefined}
            inert={!open}
            onTransitionEnd={(event) => {
              if (
                !open &&
                event.target === event.currentTarget &&
                event.propertyName === "opacity"
              )
                setPresent(false);
            }}
            aria-label={title}
            style={{
              backdropFilter: theme.panelBackdrop,
              background: theme.panelBg,
              // 面板不描边，只靠一层柔和阴影抬起；边框留给请求卡、输入框这些内部元素。
              boxShadow: fill
                ? "none"
                : theme.dark
                  ? "0 12px 32px rgba(0,0,0,.18)"
                  : "0 12px 32px rgba(28,27,26,.10)",
              boxSizing: "border-box",
              position: "relative",
              flexDirection: "column",
              height: fill ? "100%" : height,
              marginBottom: -panelOffset,
              minHeight: 0,
              overflow: "hidden",
              transformOrigin: `${alignRight ? "right" : "left"} calc(100% - ${String(panelOffset)}px)`,
              WebkitBackdropFilter: theme.panelBackdrop,
              width: "100%",
            }}
          >
            {!fill && onResize && (
              <>
                <PanelResize alignRight={alignRight} onResize={onResize} />
                <PanelResize
                  alignRight={alignRight}
                  heightOnly
                  onResize={onResize}
                />
              </>
            )}
            <SupportNotice tokens={theme} />
            <SupportRequestPanel tokens={theme} />
            {drawerHandle}
            <PanelHeader
              theme={theme}
              radius={radius ?? 8}
              title={title}
              subtitle={subtitle}
              avatar={avatar}
              drawer={drawerHandle !== undefined}
              fullscreen={fullscreen}
              reserveSide={
                reserveHeaderSpace ? (alignRight ? "left" : "right") : undefined
              }
              onClear={onClear}
              onClose={onClose}
              onToggleFullscreen={onToggleFullscreen}
            />

            {empty ? (
              <div
                class="gn-welcome"
                data-mobile={mobile || undefined}
                data-base-ui-swipe-ignore={drawerHandle ? "" : undefined}
                style={{
                  ...scrollStyle,
                  display: "flex",
                  flex: 1,
                  flexDirection: "column",
                  gap: 16,
                  minHeight: 0,
                  overflow: "auto",
                  padding: "28px 16px 12px",
                }}
              >
                <div class="gn-welcome-hero">
                  <div
                    class="gn-welcome-stage"
                    style={{ "--gn-welcome-halo": theme.accentFill }}
                  >
                    {/* 按最终尺寸绘制，避免放大带滤镜的合成层造成边缘模糊。 */}
                    <WidgetCharacter
                      size={mobile ? 72 : 96}
                      color={theme.accentFill}
                      eyeColor={theme.accentFillFg}
                      radius={`calc(${characterRadius} * ${mobile ? "1.8" : "2.4"})`}
                      motion="quiet"
                      variant={panelCharacter}
                    />
                  </div>
                  <div
                    style={{
                      animation:
                        "gnMsgIn .24s .34s cubic-bezier(.2,.8,.2,1) both",
                      fontSize: 22,
                      fontWeight: theme.serif ? 400 : 500,
                      letterSpacing: "-.02em",
                      lineHeight: 1.25,
                      maxWidth: 240,
                      textAlign: "center",
                      textWrap: "balance",
                    }}
                  >
                    {emptyTitle}
                  </div>
                </div>
                {suggestions.length > 0 && (
                  <div
                    class="gn-suggestions"
                    data-stacked={
                      suggestions.some(
                        (suggestion) =>
                          suggestion.length > SUGGESTION_PILL_MAX_CHARS,
                      ) || undefined
                    }
                    style={{
                      "--gn-chip-bg": theme.dark
                        ? withAlpha(theme.accentFill, 0.18)
                        : `color-mix(in srgb, ${theme.accentFill} 8%, #FFFFFF)`,
                      "--gn-chip-fg": theme.linkText,
                      "--gn-chip-hover-bg": theme.accentFill,
                      "--gn-chip-hover-fg": theme.accentFillFg,
                      "--gn-chip-radius": theme.chipRadius,
                      "--gn-chip-stacked-radius": `min(18px, ${theme.chipRadius})`,
                      fontWeight: theme.serif ? 400 : 500,
                    }}
                  >
                    {suggestions.map((suggestion, index) => (
                      <button
                        // 不拿问题文本当 key：后台预览喂的草稿里可能暂时有两条一样的。
                        key={index}
                        class="gn-suggestion"
                        disabled={busy}
                        onClick={() => {
                          onQuickReply?.(suggestion);
                        }}
                        style={{
                          animationDelay: `${String(
                            SUGGESTION_DELAY_BASE_MS +
                              index * SUGGESTION_DELAY_STEP_MS,
                          )}ms`,
                        }}
                        type="button"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
                {error && (
                  <p
                    role="alert"
                    style={{ color: theme.dangerText, margin: 0 }}
                  >
                    {error}
                  </p>
                )}
              </div>
            ) : (
              <MessageScroller.Provider
                autoScroll={following}
                defaultScrollPosition={waking ? "end" : "last-anchor"}
                scrollPreviousItemPeek={16}
                scrollEdgeThreshold={SCROLL_EDGE_THRESHOLD}
              >
                <MessageScroller.Root
                  data-base-ui-swipe-ignore={drawerHandle ? "" : undefined}
                  style={{
                    display: "flex",
                    flex: 1,
                    minHeight: 0,
                    position: "relative",
                  }}
                >
                  <MessageViewport
                    className="gn-message-viewport"
                    aria-label={m.widget_conversation_messages()}
                    style={{
                      ...scrollStyle,
                      display: "flex",
                      flex: 1,
                      flexDirection: "column",
                      gap: 10,
                      minHeight: 0,
                      overflow: "auto",
                      overflowAnchor: "none",
                    }}
                  >
                    <MessageScroller.Content
                      aria-busy={busy}
                      className="gn-message-list"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        flexShrink: 0,
                        minHeight: "100%",
                        boxSizing: "border-box",
                        minWidth: 0,
                        padding: theme.editorial ? "20px 18px" : "18px 16px",
                      }}
                    >
                      {rows.map(
                        (
                          {
                            resolved,
                            groupStart,
                            turnStart,
                            copyText,
                            live,
                            landing,
                            reveal,
                          },
                          index,
                        ) => {
                          const message = resolved.message;
                          const receipt = message.cartReceipt;
                          if (message.role === "system" && receipt)
                            return (
                              <MessageScroller.Item
                                key={message.id}
                                messageId={message.id}
                                data-turn-start={turnStart || undefined}
                              >
                                <div
                                  class="gn-cart-receipt-row"
                                  data-wake={resolved.wake || undefined}
                                  style={{ "--gn-enter-delay": resolved.delay }}
                                >
                                  <CartReceipt
                                    cartHref={
                                      storefront
                                        ? `${storefront.rootUrl}cart`
                                        : undefined
                                    }
                                    editDisabled={cart.openers?.busy}
                                    onEdit={
                                      cart.openers &&
                                      ((trigger) => {
                                        cart.openers?.openEdit(
                                          receipt,
                                          trigger,
                                        );
                                      })
                                    }
                                    receipt={receipt}
                                    tokens={resolved.tokens}
                                  />
                                </div>
                              </MessageScroller.Item>
                            );
                          if (message.role === "system")
                            return (
                              <MessageScroller.Item
                                key={message.id}
                                messageId={message.id}
                                data-turn-start={turnStart || undefined}
                              >
                                <div
                                  class="gn-system-notice"
                                  data-wake={resolved.wake || undefined}
                                  style={{ "--gn-enter-delay": resolved.delay }}
                                >
                                  <span
                                    style={{
                                      background: theme.bubbleBg,
                                      borderRadius: 999,
                                      color: theme.muted,
                                      fontSize: 12,
                                      lineHeight: 1.4,
                                      maxWidth: "100%",
                                      overflow: "hidden",
                                      padding: "4px 11px",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {m.widget_notice_now_viewing({
                                      title: message.text ?? "",
                                    })}
                                  </span>
                                </div>
                              </MessageScroller.Item>
                            );
                          return (
                            <MessageScroller.Item
                              key={message.id}
                              messageId={message.id}
                              scrollAnchor={message.role === "user"}
                              data-turn-start={turnStart || undefined}
                            >
                              <WidgetMessage
                                m={resolved}
                                groupStart={index === 0 || turnStart}
                                characterMotion={
                                  index === activeAvatar
                                    ? avatarMotion
                                    : undefined
                                }
                                showAvatar={groupStart}
                                landing={landing}
                                characterVariant={panelCharacter}
                                cart={cart.openers}
                                onRetry={onRetry}
                                onOrderAction={onOrderAction}
                                orderBusy={orderBusy}
                                orderFailures={orderFailures}
                                onVerifyTask={onVerifyTask}
                                verificationBusy={verificationBusy}
                                playgroundVerification={playgroundVerification}
                                verificationAuth={verificationAuth}
                                onVerifyEmail={onVerifyEmail}
                                reveal={reveal}
                                feedbackEdge={merchantFeedback?.align}
                                actions={
                                  copyText !== "" &&
                                  (replyCopyButton || merchantFeedback) ? (
                                    <MessageActions
                                      delay={
                                        reveal
                                          ? REPLY_ACTIONS_DELAY
                                          : resolved.delay
                                      }
                                      edge={merchantFeedback?.align ?? "start"}
                                      snapshot={feedbackSnapshots[message.id]}
                                      onLikeError={
                                        merchantFeedback?.onLikeError
                                      }
                                      onLoadReasons={
                                        merchantFeedback?.loadReasons
                                      }
                                      text={
                                        replyCopyButton ? copyText : undefined
                                      }
                                      onSubmit={
                                        merchantFeedback &&
                                        (async (draft) => {
                                          const record =
                                            await merchantFeedback.submit(
                                              message.id,
                                              draft,
                                            );
                                          setFeedbackSnapshots((current) => ({
                                            ...current,
                                            [message.id]: record,
                                          }));
                                          return record;
                                        })
                                      }
                                    />
                                  ) : undefined
                                }
                              />
                              {index === lastSpoken &&
                                !message.typing &&
                                !message.alert &&
                                waitingLabel && (
                                  <Marker
                                    class={
                                      message.role === "ai"
                                        ? "gn-followup gn-message-followup"
                                        : "gn-followup"
                                    }
                                    style={{
                                      paddingTop: 10,
                                      animationDelay:
                                        live || resolved.wake
                                          ? resolved.delay
                                          : `${String(280 + Math.min(index + 1, 8) * 60)}ms`,
                                    }}
                                  >
                                    <MarkerContent>
                                      <span
                                        style={{
                                          color: theme.muted,
                                          fontSize: 12,
                                        }}
                                      >
                                        {waitingLabel}
                                      </span>
                                    </MarkerContent>
                                  </Marker>
                                )}
                              {index === lastSpoken &&
                                quickReplies.length > 0 && (
                                  <QuickReplies
                                    theme={theme}
                                    replies={quickReplies}
                                    busy={busy}
                                    onPick={onQuickReply}
                                    animationDelay={
                                      resolved.wake
                                        ? resolved.delay
                                        : QUICK_REPLY_DELAY
                                    }
                                  />
                                )}
                            </MessageScroller.Item>
                          );
                        },
                      )}
                    </MessageScroller.Content>
                  </MessageViewport>
                  <MessageLatestButton
                    className="gn-scroll-latest"
                    onClick={() => {
                      setFollowedTurn(turnId);
                    }}
                    aria-label={m.widget_conversation_latest()}
                    // 手机上面板窄，居中会压住消息正文（GON-158），改为靠右和消息列表右侧内边距对齐；桌面仍居中。
                    style={{
                      position: "absolute",
                      bottom: 8,
                      ...(mobile
                        ? { right: theme.editorial ? 18 : 16 }
                        : { left: "50%", transform: "translateX(-50%)" }),
                      background: theme.cardBg,
                      border: `1px solid ${theme.line}`,
                      borderRadius: "50%",
                      color: theme.ink,
                      cursor: "pointer",
                      width: 32,
                      height: 32,
                      padding: 0,
                    }}
                  >
                    <ArrowDownIcon size={16} />
                  </MessageLatestButton>
                  <FollowCartReceipt id={lastReceiptId} />
                  <FollowActionCards action={cardAction} messages={messages} />
                </MessageScroller.Root>
              </MessageScroller.Provider>
            )}

            <Composer
              theme={theme}
              draft={draft}
              onDraftChange={setDraft}
              onSubmit={submit}
              canSend={canSend}
              busy={busy}
              placeholder={placeholder}
              inputRef={inputRef}
              mobile={mobile}
              drawer={drawerHandle !== undefined}
              justSent={busy && reaction === "ack"}
              poweredBy={poweredBy}
              voiceEnabled={voiceEnabled}
              voiceStatus={voiceStatus}
              voiceError={voiceError}
              onVoiceToggle={onVoiceToggle}
            />
            {cart.layer}
          </section>
        </PanelSheetHostContext.Provider>
      )}

      {renderLauncher && (
        <LauncherCapsule
          position={position}
          size={capsule.size}
          open={open}
          present={present}
          prompt={prompt}
          bodyPrompt={bodyPrompt}
          drag={launcherDrag}
          launcherLabel={launcherLabel}
          onOpen={onOpen}
          onClose={onClose}
          character={entryCharacter}
          motion={
            busy
              ? "working"
              : error
                ? "blocked"
                : reaction === "done"
                  ? "done"
                  : prompt || open
                    ? "quiet"
                    : "idle"
          }
          theme={theme}
          characterRadius={characterRadius}
          promptRadius={`${String(Math.min((radius ?? 8) * 1.8, 28))}px`}
          glassBubble={glassBubble}
        />
      )}
    </div>
  );
}
